import dayjs from 'dayjs'
import Styles from './CalendarEventCard.module.scss'
import { Text } from 'blaze-ui/Text/Text'
import { Image } from './Image'
import { CSSProperties, useRef, useState } from 'react'
import cx from 'classnames'
import { AlmanacFileCalendarEvent } from 'screens/Calendar/types'
import { HoverOverlay } from './HoverOverlay/HoverOverlay'
import { useHover } from '@react-aria/interactions'
import { LoadingShimmerer } from 'components/LoadingShimmerer/LoadingShimmerer'
import { useProgress } from './useProgress'

import { useBlogIntegrations } from 'hooks/useBlogIntegrations'
import { useEmailIntegrations } from 'hooks/useEmailIntegrations'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { useStatus } from 'hooks/calendar/useStatus'
import { useValidationErrors } from 'hooks/calendar/useValidationErrors'
import { CalendarItemChannelLabel } from 'components/CalendarItemChannelLabel/CalendarItemChannelLabel'
import { getEventProperties } from './utils'
import { useShowNextGenContentPreferences } from 'hooks/useShowNextGenContentPreferences'
import { STEP_ICONS } from 'components/AgenticStepIndicator/stepIcons'
import { PostStatusBadge } from './PostStatusBadge'
import { useFlipperFlag } from 'hooks/useFlipperFlag'

const RENDERING_IMAGE_PROGRESS = {
  current_step: 'rendering_image',
  step_label: 'Rendering image',
}

export interface CalendarEventCardProps {
  event: AlmanacFileCalendarEvent
  cardHeight?: number
  cardWidth: number
  previewWidth?: number
  isSelectView?: boolean
  isSelected?: boolean
  onSelect?: (id: string) => void
  onEventClick?: (event: AlmanacFileCalendarEvent) => void
  loadingColor: string
  hasInnerPreview?: boolean
  innerPreviewV2?: boolean
  showStatus?: boolean
  maxDocPreviewLines?: number
  previewBorderRadius?: number
  canSelect?: boolean
  dateFormat?: string
  allowExpandCaption?: boolean
  showHoverOverlay?: boolean
  statusPillSize?: 'xs' | 'sm' | 'md'
  cardBorder?: string
  forceInnerPreviewWidth?: number | string
  omitFeedPrefix?: boolean
  showHeader?: boolean
  /**
   * Home "Upcoming posts" variant: render a single centered "Preview" pill on hover (which opens
   * the full-screen preview via `onEventClick`) instead of the calendar's corner action buttons.
   */
  showCenteredPreviewButton?: boolean
}

export const CalendarEventCard = ({
  event,
  cardWidth,
  cardHeight,
  previewWidth,
  isSelectView = false,
  isSelected = false,
  onSelect,
  onEventClick,
  loadingColor,
  hasInnerPreview = false,
  showStatus = true,
  maxDocPreviewLines,
  previewBorderRadius = 0,
  canSelect = true,
  dateFormat = 'h:mma',
  allowExpandCaption = true,
  showHoverOverlay = true,
  statusPillSize = 'md',
  cardBorder,
  innerPreviewV2,
  forceInnerPreviewWidth,
  omitFeedPrefix,
  showHeader = true,
  showCenteredPreviewButton = false,
}: CalendarEventCardProps) => {
  const workspace = useCurrentWorkspace()
  const { hoverProps, isHovered } = useHover({})
  const { start, channel, created_at, image_url, empty, design_content } = event
  const [imageLoaded, setImageLoaded] = useState(false)
  const [designLoaded, setDesignLoaded] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)

  const { connectedBlogIntegration } = useBlogIntegrations()
  const { connectedEmailIntegration } = useEmailIntegrations()
  const { data: brandPlan } = useCurrentBrandPlan()
  const isV2 = brandPlan?.has_content_strategies

  const { showNextGenContentPreferences: isGenV2 } = useShowNextGenContentPreferences()
  const isAIGeneratedVideo = !isGenV2 && event.is_ai_generated_video

  const { progress } = useProgress({ date: created_at })

  const { validationErrors } = useValidationErrors({ event })
  const {
    didGenerationFail,
    isApproved,
    isDraft,
    isFailed,
    isHybrid,
    isNew,
    isPosted,
    isUpdating,
    isWarning,
    isPaused,
    needsApproval,
    needsConnect,
  } = useStatus({
    event,
    validationErrors,
    workspace,
    connectedBlogIntegration,
    connectedEmailIntegration,
  })

  const isCopilot = !workspace?.is_virtual_marketer
  const calendarV2 = useFlipperFlag('calendar_v2')
  const showRenderingImageOverlay = calendarV2 && isUpdating
  const progressData = showRenderingImageOverlay ? event.progress || RENDERING_IMAGE_PROGRESS : event.progress
  const stepIcon = progressData?.current_step ? STEP_ICONS[progressData.current_step] : undefined
  const { isArticlePreview, isLoading, isLoadingDocPreview, isWithinPreviewImageWaitTime, showDocText } =
    getEventProperties(event, imageLoaded, designLoaded, isCopilot, calendarV2)

  if (!event.channel) return null

  const isGenerating = calendarV2 ? !!event.is_loading : event.is_generating
  const isGeneratingWithContent = isGenerating && !!event.image_url
  const isPreviewDoneGenerating = !isLoading || !isWithinPreviewImageWaitTime || isGeneratingWithContent
  const canOpenPreview = (!event.scheduleContentId || event.key) && (isV2 || isPreviewDoneGenerating)

  const showBadge =
    showStatus &&
    !isLoading &&
    !didGenerationFail &&
    (isNew ||
      isPosted ||
      isFailed ||
      needsConnect ||
      isWarning ||
      (isUpdating && !showRenderingImageOverlay) ||
      isDraft ||
      needsApproval ||
      isApproved ||
      isPaused)

  const classes =
    isLoading || !showStatus
      ? {}
      : {
          [Styles.failed]: isFailed,
          [Styles.posted]: isPosted,
          [Styles.warning]: isWarning,
          [Styles.new]: isNew,
          [Styles.isV2]: isV2,
          [Styles.canOpenPreview]: canOpenPreview,
        }

  const innerPreviewClasses = !hasInnerPreview
    ? {}
    : {
        [Styles.innerEventPreview]: !showDocText && (isArticlePreview || !!image_url || !!design_content || !!empty),
        [Styles.innerPreviewV2]: innerPreviewV2,
        [Styles.loadingInnerPreview]:
          isWithinPreviewImageWaitTime &&
          (isLoadingDocPreview || isLoading || (!image_url && !design_content && !showDocText && !empty)),
        [Styles.innerDocEventPreview]: showDocText,
      }

  const contentWidth = cardRef.current?.getBoundingClientRect().width || 0
  const innerPreviewWidth = previewWidth || cardWidth

  const forceCardWidthValue =
    typeof forceInnerPreviewWidth === 'string' ? forceInnerPreviewWidth : `${forceInnerPreviewWidth}px`

  return (
    <div
      key={event.key}
      {...hoverProps}
      ref={cardRef}
      data-status-failed={isFailed || isHybrid}
      className={cx(Styles.eventContainer, classes, {
        [Styles.hasInnerPreview]: hasInnerPreview,
        [Styles.innerPreviewV2]: innerPreviewV2,
      })}
      style={
        {
          '--card-width': `${forceInnerPreviewWidth ? forceCardWidthValue : cardWidth}px`,
          '--card-height': `${cardHeight}px`,
          '--inner-preview-width': `${innerPreviewWidth}px`,
          border: cardBorder,
        } as CSSProperties
      }
    >
      {canOpenPreview && showHoverOverlay && (
        <HoverOverlay
          event={event}
          isHovered={isHovered}
          isSelectView={isSelectView}
          isSelected={isSelected}
          onSelect={onSelect}
          onClick={() => onEventClick?.(event)}
          canSelect={canSelect && isPreviewDoneGenerating}
          showCenteredPreviewButton={showCenteredPreviewButton}
        />
      )}
      {showHeader && (
        <div
          className={cx(Styles.eventHeader, {
            [Styles.aiVideoHeader]: isAIGeneratedVideo,
            [Styles.innerPreviewV2]: innerPreviewV2,
          })}
        >
          <CalendarItemChannelLabel
            brandPlan={brandPlan}
            channel={channel ?? undefined}
            event={event}
            isAIGeneratedVideo={!!isAIGeneratedVideo}
            isHovered={isHovered}
            omitFeedPrefix={omitFeedPrefix}
          />
          <div className={cx(Styles.dateContainer, { [Styles.timeDateContainer]: dateFormat === 'MMM DD h:mma' })}>
            <Text variant="label">{start ? dayjs(start).format(dateFormat) : 'Not Scheduled'}</Text>
          </div>
        </div>
      )}
      <div className={cx(Styles.eventPreview, innerPreviewClasses)}>
        <div className={Styles.eventPreviewContent}>
          <LoadingShimmerer
            borderRadius={previewBorderRadius}
            show={isUpdating && !isLoading && !showRenderingImageOverlay}
            isOverlay
            className={cx({ [Styles.innerPreviewLoadingShimmerer]: hasInnerPreview })}
          >
            <Image
              event={event}
              onImageLoaded={setImageLoaded}
              imageLoaded={imageLoaded}
              loadingColor={loadingColor}
              loadingProgress={progress}
              stepLabel={progressData?.step_label}
              stepIcon={stepIcon}
              referenceImageUrl={event.reference_image_url}
              containerRef={cardRef}
              cardWidth={previewWidth || contentWidth || cardWidth}
              previewWidth={previewWidth}
              designLoaded={designLoaded}
              onDesignLoaded={setDesignLoaded}
              maxDocPreviewLines={maxDocPreviewLines}
              allowExpandCaption={allowExpandCaption}
              didGenerationFail={didGenerationFail}
              isCopilot={isCopilot}
              generationStartedAt={created_at}
              showRenderingImageOverlay={showRenderingImageOverlay}
            />
          </LoadingShimmerer>
        </div>
      </div>
      {showBadge && (
        <div className={Styles.statusPillContainer}>
          <PostStatusBadge event={event} size={statusPillSize} />
        </div>
      )}
    </div>
  )
}
